import { useEffect, useRef, useState } from "react";

import { api } from "../api";
import type { JobInfo } from "../types";

// Сразу после POST /api/jobs первый GET может прийти раньше, чем созданная
// задача "видна" (in-memory store на другой реплике, 502 от Traefik во
// время rolling-update и т.п.). Терпим любые ошибки в первые секунды и
// держим лоадер, чтобы пользователь не видел страницу "Ошибка 404".
const INITIAL_GRACE_MS = 6000;
const FAST_POLL_MS = 300;
const FAST_POLL_DURATION_MS = 3000;
const NOT_FOUND_RE = /^404:/;

export function useJobPolling(jobId: string | null, intervalMs = 1500) {
  const [job, setJob] = useState<JobInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stopRef = useRef(false);
  const hasJobRef = useRef(false);

  useEffect(() => {
    setJob(null);
    setError(null);
    hasJobRef.current = false;
    if (!jobId) {
      return;
    }
    stopRef.current = false;
    let timer: number | null = null;
    const startedAt = Date.now();

    const tick = async () => {
      try {
        const info = await api.getJob(jobId);
        if (stopRef.current) return;
        hasJobRef.current = true;
        setError(null);
        setJob(info);
        if (info.status === "done" || info.status === "failed") {
          return;
        }
      } catch (e) {
        if (stopRef.current) return;
        const message = e instanceof Error ? e.message : String(e);
        const inGracePeriod = Date.now() - startedAt < INITIAL_GRACE_MS;
        const isNotFound = NOT_FOUND_RE.test(message);
        // 1) грейс-период до первого успешного ответа — глотаем всё;
        // 2) после получения job 404 от "не той" реплики тоже игнорим,
        //    чтобы не мерцать "Ошибка" на работающей странице.
        const swallow =
          (inGracePeriod && !hasJobRef.current) ||
          (isNotFound && hasJobRef.current);
        if (!swallow) {
          setError(message);
        }
      }
      const delay =
        Date.now() - startedAt < FAST_POLL_DURATION_MS ? FAST_POLL_MS : intervalMs;
      timer = window.setTimeout(tick, delay);
    };

    void tick();

    return () => {
      stopRef.current = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [jobId, intervalMs]);

  return { job, error };
}
